import React, { useLayoutEffect, useRef, useState } from 'react'
import { line as d3line } from 'd3-shape'
import { Box, Button, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded'
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded'
import { advancedColors } from '../theme'
import SurveyCompletionTable from './SurveyCompletionTable'
import { ScoredInstance, completionDayLabel, completionTimeLabel } from './questionnaireHistory'

type Props = {
  instances: ScoredInstance[]
  timezone: string | null | undefined
  selectedId: string
  onSelect: (id: string) => void
  onTableExpandedChange?: (expanded: boolean) => void
}

const SurveyCompletionHistory = ({ instances, timezone, selectedId, onSelect, onTableExpandedChange }: Props) => {
  const [view, setView] = useState<'chart' | 'table'>('chart')
  const [tableExpanded, setTableExpanded] = useState(false)
  const changeExpansion = (expanded: boolean) => {
    setTableExpanded(expanded)
    onTableExpandedChange?.(expanded)
  }
  const chartViewport = useRef<HTMLDivElement>(null)
  const newestId = instances[0]?.id
  const chronological = [...instances].reverse()
  const width = Math.max(560, chronological.length * 48)
  const height = 220
  const padding = { left: 36, right: 28, top: 28, bottom: 36 }
  const maxScore = Math.max(1, ...instances.map((instance) => instance.score ?? 0))
  const x = (index: number) =>
    padding.left + ((index + 0.5) / chronological.length) * (width - padding.left - padding.right)
  const y = (score: number) => height - padding.bottom - (score / maxScore) * (height - padding.top - padding.bottom)

  const path = d3line<ScoredInstance>()
    .defined((instance) => instance.score !== null)
    .x((_instance, index) => x(index))
    .y((instance) => y(instance.score ?? 0))(chronological)

  useLayoutEffect(() => {
    const viewport = chartViewport.current
    if (viewport) viewport.scrollLeft = viewport.scrollWidth - viewport.clientWidth
  }, [view, newestId, width])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: tableExpanded ? '1 1 auto' : '0 0 auto' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
          flexShrink: 0,
          backgroundColor: advancedColors.surface,
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          aria-label="Completion history view"
          sx={{ '& .MuiToggleButton-root': { height: 28, py: 0, px: 1.25, fontSize: 12 } }}
          onChange={(_event, next: 'chart' | 'table' | null) => {
            if (next) {
              setView(next)
              if (next === 'chart') changeExpansion(false)
            }
          }}
        >
          <ToggleButton value="chart">Chart</ToggleButton>
          <ToggleButton value="table">Table</ToggleButton>
        </ToggleButtonGroup>
        {view === 'table' && (
          <Button
            size="small"
            aria-expanded={tableExpanded}
            sx={{ height: 28, py: 0 }}
            startIcon={tableExpanded ? <UnfoldLessRoundedIcon /> : <UnfoldMoreRoundedIcon />}
            onClick={() => changeExpansion(!tableExpanded)}
          >
            {tableExpanded ? 'Collapse table' : 'Expand table'}
          </Button>
        )}
      </Box>

      {view === 'chart' && (
        <>
          <Box ref={chartViewport} sx={{ overflowX: 'auto' }}>
            <Box
              component="svg"
              role="group"
              aria-label="Survey completion chart"
              viewBox={`0 0 ${width} ${height}`}
              sx={(theme) => ({
                display: 'block',
                width: '100%',
                minWidth: width,
                fontFamily: theme.typography.fontFamily,
              })}
            >
              {[0, maxScore / 2, maxScore].map((tick) => (
                <g key={tick}>
                  <line
                    x1={padding.left}
                    x2={width - padding.right}
                    y1={y(tick)}
                    y2={y(tick)}
                    stroke={advancedColors.line}
                    strokeDasharray="3 4"
                  />
                  <text
                    x={padding.left - 8}
                    y={y(tick) + 4}
                    textAnchor="end"
                    fontSize={11}
                    fill={advancedColors.inkMuted}
                  >
                    {Math.round(tick * 10) / 10}
                  </text>
                </g>
              ))}
              <path d={path ?? ''} fill="none" stroke={advancedColors.green} strokeWidth={2} />
              {chronological.map((instance, index) => {
                if (instance.score === null) return null
                const label = `${completionTimeLabel(instance, timezone)} · Score ${instance.score}`
                return (
                  <Tooltip key={instance.id} title={label} describeChild>
                    <g
                      role="button"
                      tabIndex={0}
                      aria-label={label}
                      aria-pressed={selectedId === instance.id}
                      onClick={() => onSelect(instance.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onSelect(instance.id)
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <circle cx={x(index)} cy={y(instance.score)} r={16} fill="transparent" />
                      <circle
                        cx={x(index)}
                        cy={y(instance.score)}
                        r={selectedId === instance.id ? 7 : 5}
                        fill={selectedId === instance.id ? advancedColors.green : advancedColors.surface}
                        stroke={advancedColors.green}
                        strokeWidth={2}
                      />
                      <text
                        x={x(index)}
                        y={y(instance.score) - 14}
                        textAnchor="middle"
                        fontSize={12}
                        fontWeight={600}
                        fill={advancedColors.ink}
                      >
                        {instance.score}
                      </text>
                    </g>
                  </Tooltip>
                )
              })}
              {[0, chronological.length - 1]
                .filter((value, index, values) => values.indexOf(value) === index)
                .map((index) => (
                  <text
                    key={chronological[index].id}
                    x={x(index)}
                    y={height - 10}
                    textAnchor="middle"
                    fontSize={11}
                    fill={advancedColors.inkMuted}
                  >
                    {completionDayLabel(chronological[index], timezone)}
                  </text>
                ))}
            </Box>
          </Box>
          {chronological
            .filter((instance) => instance.score === null)
            .map((instance) => (
              <Button key={instance.id} size="small" onClick={() => onSelect(instance.id)}>
                {completionTimeLabel(instance, timezone)} · Score unavailable
              </Button>
            ))}
        </>
      )}

      {view === 'table' && (
        <Box
          role="region"
          aria-label="Scrollable survey completions"
          tabIndex={0}
          sx={{
            height: tableExpanded ? 520 : 220,
            maxHeight: tableExpanded ? '60dvh' : '28dvh',
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <SurveyCompletionTable
            instances={instances}
            timezone={timezone}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        </Box>
      )}
    </Box>
  )
}

export default SurveyCompletionHistory
